// The need score (stream-need-score, APP_PLAYER §7 "The asset queue"): which
// queued `bulk` fetch goes next, and which one is never fetched. PURE (no
// three, no DOM, no fetch): assetq.js ranks its bulk queue with it, main.js
// feeds it the view (needTick), scripts/check-needscore.mjs pins every rule
// (a red twin each) and simulates the boot queue on a live bake.
//
//   need  { rect: [x0, z0, x1, z1] | null, m: metres | null, bytes: n | null }
//         rect   the asset's footprint in game metres; null = world-wide (a
//                phase-1 file: in view, and whole, from every pose)
//         m      the size of what it draws (default: the rect's longest side)
//         bytes  its size when known up front, else cfg.bytes_unknown
//         keep   never dropped (the tiled ring's optic meshes: the zoom
//                wedge asked for them at the optic's pixel angle)
//   view  { eye: {x, z}, pa: rad per px, px: view height px, aspect,
//           dir: {x, z} unit | null } — or null: no target yet (Map first)
//
// THE RULES (each one a check-needscore row):
//   px      the projected size, pixels: (m / max(d, 1) / pa)^2 capped at the
//           view (px^2 x aspect); world-wide, or no view: the whole view.
//           d = the eye's horizontal distance to the rect (ringtiers rectDist)
//   score   px / bytes
//   inView  the rect meets the view's horizontal wedge (dir +- half the
//           horizontal fov + cfg.margin_deg); the eye inside the rect, no dir
//           (before entry a target has no heading) or no view: in view
//   order   aged first (FIFO), then in view, then the higher score, then the
//           request order —
//           so with no view, and among world-wide files of unknown size, the
//           queue is FIFO: the boot order main.js chose, as before
//   age     a bulk fetch queued >= cfg.age_s (wall seconds) goes before every
//           younger one, the aged among themselves in request order — no
//           asset (a far-ring file, world-wide and scored as one
//           unknown-size file) starves behind need-scored tiles; the wait is
//           bounded by age_s + the older aged ones (needAged, assetq _pump)
//   drop    OFF unless cfg.drop === true (ringtiers.json ships need.drop
//           false): with a view only, the whole extent m at its nearest
//           distance is within one pixel — ringtiers.js withinPixel, THE rule
//           the ring tiers' genLevel uses too (here k = cfg.texel_px) — never
//           fetched (the queue resolves it null; a
//           `status` caller sees dropped + gen). The consumers re-ask a
//           dropped asset they still want once the view moved on (assetq
//           needGen / onNeedView; need-drop-rerequest): ring.js tiled player
//           / optic sectors, surface.js far slots (no RETRY_MS for a drop).
//           Still shipped off until the owner's visual read.
//
// Only `bulk` is ever scored: `map` and `near` (the Map, and what the player
// needs to STAND) bypass the queue, never wait behind bulk, never drop.
import { rectDist, withinPixel } from './ringtiers.js';

export const NEED_DEFAULTS = { enabled: true, drop: false, texel_px: 1, bytes_unknown: 1048576, margin_deg: 10, aspect: 16 / 9, age_s: 30 };

const cfgOf = (cfg) => ({ ...NEED_DEFAULTS, ...(cfg || {}) });

const rectOk = (r) => (Array.isArray(r) && r.length === 4 && r.every(Number.isFinite) && r[2] > r[0] && r[3] > r[1]
  ? r : null);

// the bearing interval of rect r from (x, z) overlaps dir +- half (rad)
function inWedge(x, z, r, dir, half) {
  const yaw = Math.atan2(dir.x, -dir.z);
  const rel = [[r[0], r[1]], [r[2], r[1]], [r[0], r[3]], [r[2], r[3]]].map(([cx, cz]) => {
    const a = Math.atan2(cx - x, -(cz - z)) - yaw;
    return Math.atan2(Math.sin(a), Math.cos(a));
  });
  const lo = Math.min(...rel), hi = Math.max(...rel);
  // the eye is outside the rect, so it spans < pi: a wider spread is the one
  // that wraps behind (+-pi) — then it covers [the smallest positive bearing,
  // pi] and [-pi, the largest negative one] (NOT [hi, pi] / [-pi, lo]: those
  // are the corners nearest the back, review need-fix #1)
  if (hi - lo <= Math.PI) return hi >= -half && lo <= half;
  const pos = rel.filter((a) => a >= 0), neg = rel.filter((a) => a < 0);
  return Math.min(...pos) <= half || Math.max(...neg) >= -half;
}

// -> { px, score, inView, drop, d } for need `n` (null = world-wide) at view `v`
export function needOf(n, v, cfg) {
  const c = cfgOf(cfg);
  if (c.enabled === false) v = null;     // ringtiers.json need.enabled false: FIFO, nothing dropped
  const aspect = v && v.aspect > 0 ? v.aspect : c.aspect;
  const H = v && v.px > 0 ? v.px : 1;
  const area = H * H * aspect;
  const rect = n ? rectOk(n.rect) : null;
  const bytes = n && n.bytes > 0 ? n.bytes : c.bytes_unknown;
  // no view (Map first, before a target): nothing to rank on — one score
  // for all, the request order decides
  // (a non-finite eye is no view either: NaN would poison the order)
  if (!v || !v.eye || !Number.isFinite(v.eye.x) || !Number.isFinite(v.eye.z) || !(v.pa > 0)) {
    return { px: area, score: 1, inView: true, drop: false, d: null };
  }
  if (!rect) return { px: area, score: area / bytes, inView: true, drop: false, d: null };
  const d = rectDist(v.eye.x, v.eye.z, rect);
  const m = n.m > 0 ? n.m : Math.max(rect[2] - rect[0], rect[3] - rect[1]);
  const lin = m / Math.max(d, 1) / v.pa;
  const px = Math.min(lin * lin, area);
  // pa = 2 tan(vfov / 2) / H, so tan(hfov / 2) = pa H / 2 x aspect
  const half = Math.atan((v.pa * H / 2) * aspect) + (c.margin_deg * Math.PI) / 180;
  const inView = d === 0 || !v.dir || half >= Math.PI || inWedge(v.eye.x, v.eye.z, rect, v.dir, half);
  // n.keep (the tiled ring's optic meshes): never dropped — the zoom wedge
  // asked for it at the optic's pixel angle; the naked-eye pa is not its rule
  const drop = c.drop === true && !n.keep && withinPixel(m, d, v.pa, c.texel_px);
  return { px, score: px / bytes, inView, drop, d };
}

// The re-score hysteresis (main.js needTick, review need-fix #2): view `v`
// {x, z, yaw (rad, null = no heading), pa} is due a re-score against the
// last SCORED one `last` (null: never scored) on a move >= move_m, a turn
// >= 15 deg, a heading appearing / going, or a pixel-angle change >= 10 % —
// measured from the last scored view, never a bucket edge, so a head
// trembling at 7.5 deg or a zoom ease does not re-score every frame. No
// zoomGen term: needOf reads pa, which a flat zoom moves (in VR pa is the
// class table and a zoom changes nothing the score reads).
export const NEED_TURN = Math.PI / 12, NEED_PA_REL = 0.1;
export function needDue(last, v, moveM = 64) {
  if (!last) return true;
  if (Math.hypot(v.x - last.x, v.z - last.z) >= moveM) return true;
  if ((v.yaw == null) !== (last.yaw == null)) return true;
  if (v.yaw != null && Math.abs(Math.atan2(Math.sin(v.yaw - last.yaw), Math.cos(v.yaw - last.yaw))) >= NEED_TURN) return true;
  if (!(v.pa > 0) || !(last.pa > 0)) return !(v.pa > 0) !== !(last.pa > 0);
  return Math.abs(v.pa - last.pa) / last.pa >= NEED_PA_REL;
}

// a job queued at q (ms) has waited past cfg.age_s at now (ms)
export function needAged(q, now, cfg) {
  const a = cfgOf(cfg).age_s;
  return a > 0 && now - q >= a * 1000;
}

// the queue order over jobs {k: needOf(...), seq, old: needAged}: < 0 = a
// goes first
export function needCmp(a, b) {
  if (a.old || b.old) return a.old && b.old ? a.seq - b.seq : a.old ? -1 : 1;
  return (b.k.inView - a.k.inView) || (b.k.score - a.k.score) || (a.seq - b.seq);
}

// url -> need | null for the bulk URLs whose footprint the bake's meta names,
// so no call site changes (main.js installs it: configureAssetQueue need):
//   the tiled ring's sector meshes (meta.panorama.tiles, ringtiers.js) — the
//     sector rect (the core: world-wide); an optic mesh is `keep` (never
//     dropped: only the zoom wedge asks for it, ranked at the naked pa);
//   groundm/L<k>/<tx>_<ty>[.apron].bin — bulk beyond the frame (the far
//     tree, groundtree.js): the node square (tx / ty may be negative), keep;
//     an in-grid L0 node is bulk only in the labels' lane (main.js AimGround
//     fetchNode, rearch-r8k): its square, not keep (as the terrain/L0 tile
//     it replaces);
//   the surface tiles / shade / ways (meta.surface; bulk in the far window):
//     the tile square; shade's meta median_kb is its size.
// Everything else is world-wide (null). No byte figure is taken from the
// meta's world-wide files: it declares raw (not wire) bytes for 5 of ~17
// (ways.json 31.9 MB raw is 4.5 MB on the wire), and ranking on that would
// sink ways.json — the roads and the trails chain's root — behind every
// undeclared file. Their order stays the boot order until real wire sizes
// exist (the manifest of chunk-cache-returning).
export function needResolver(meta, baseUrl = '') {
  const pre = String(baseUrl || '').replace(/\/+$/, '') + '/';
  const files = new Map();
  const T = meta && meta.panorama && meta.panorama.tiles;
  if (T && Array.isArray(T.sectors)) {
    for (const s of T.sectors) {
      const r = s && rectOk(s.rect);
      if (!r || !s.mesh) continue;
      for (const k of ['player', 'optic']) {
        if (s.mesh[k] && typeof s.mesh[k].file === 'string') {
          files.set(s.mesh[k].file, { rect: r, m: null, bytes: null, ...(k === 'optic' ? { keep: true } : {}) });
        }
      }
    }
  }
  const L0 = meta && meta.levels && meta.levels.L0;
  const sf = meta && meta.surface;
  const sdirs = new Map();
  for (const b of sf ? [sf.tiles, sf.shade, sf.ways] : []) {
    if (b && typeof b.dir === 'string' && b.tile_m > 0) {
      sdirs.set(b.dir, { tm: b.tile_m, bytes: b.median_kb > 0 ? Math.round(b.median_kb * 1024) : null });
    }
  }
  // the ground tree's nodes + aprons (groundm/L<k>/<tx>_<ty>[.apron].bin; the
  // far tree's tx / ty may be negative): the node square, keep (the tree's
  // screen error already chose it: ranked, never dropped)
  const GM = /^groundm\/L(\d+)\/(-?\d+)_(-?\d+)(?:\.apron)?\.bin$/;
  const TILE = /^(.+)\/(\d+)_(\d+)\.[a-z0-9]+$/;
  return (url) => {
    let rel = String(url).replace(/[?#].*$/, '');
    if (pre !== '/' && rel.startsWith(pre)) rel = rel.slice(pre.length);
    const f = files.get(rel);
    if (f) return f;
    let mm = GM.exec(rel);
    if (mm) {
      const s = 128 * 2 ** +mm[1], tx = +mm[2], ty = +mm[3];
      const lane = +mm[1] === 0 && !rel.endsWith('.apron.bin') && L0 && tx >= 0 && ty >= 0 && tx < L0.nx && ty < L0.ny;
      return { rect: [tx * s, ty * s, (tx + 1) * s, (ty + 1) * s], m: null, bytes: null, ...(lane ? {} : { keep: true }) };
    }
    mm = TILE.exec(rel);
    const sd = mm && sdirs.get(mm[1]);
    if (sd) {
      const tx = +mm[2], ty = +mm[3];
      return { rect: [tx * sd.tm, ty * sd.tm, (tx + 1) * sd.tm, (ty + 1) * sd.tm], m: null, bytes: sd.bytes };
    }
    return null;
  };
}
